import React, { useState, useEffect, useRef, useContext } from 'react';

import {
	startCamera,
	connectToElement,
	setScanHandler,
	releaseScreen,
	pauseDecoding,
	resumeDecoding,
	refreshLayout,
	SCANBOT_CONTAINER_ID,
} from "../../scanSdk/scanbotSdk";

import { describePreviewState, reviveVideos } from '../../scanSdk/scanHost';
import { getCameraResolution } from '../../scanSdk/cameraResolutionSetting';

import styles from '../../styles/QRScanner.module.scss';
import {AnimatePresence, motion} from 'framer-motion';
import { useUserAPI } from '../../components/useUserAPI';
import { SharedInfoContext } from '../../components/SharedInfoContextProvider';
import { iAeonSetData } from '../../library/iaeon_sdk.js';

//スキャンを止めたまま読み取り値が来ない場合に、再開するまでの待ち時間
const SCAN_STALL_TIMEOUT = 5000;

//カメラ映像（プレビュー）が出るまでの監視間隔と待ち上限
//※黙って諦めると、スキャン枠を出す is_show が付かないまま画面が黒く残り、
//　読み込み中なのか失敗なのか分からなくなるため、超えたらエラーとして扱う
const PREVIEW_WAIT_INTERVAL = 80;
const PREVIEW_WAIT_TIMEOUT = 10000;

//プレビューが出ないときに復帰を試みるタイミング
//※スロットへの付け替えでvideoが素材を手放すと readyState が 0 のまま戻らない端末がある。
//　この場合プレビューが出ないため、映像確認後に行う refreshLayout()（＝D-21の手当て）にも
//　到達できない。安い順に、ストリームの付け直し → スキャナーの組み直し、と試す
const PREVIEW_REVIVE_AFTER = 1500;
const PREVIEW_RELAYOUT_AFTER = 3500;

function QRScanbotScanner(props) {

//スキャン画面右下の表示用（どのSDK・どの解像度で動いているか分かるようにする
const cameraResolution = getCameraResolution();

const { saleslistAPI, data: saleslistData, loading: saleslistLoading, error: saleslistError } = useUserAPI();
const { payreserveAPI, data: payreserveData, loading: payreserveLoading, error: payreserveError } = useUserAPI();
const { payAPI, data: payData, loading: payLoading, error: payError } = useUserAPI();

//取得バーコード検証用
const [QRcode, setQRcode] = useState('');

const sharedInfo = useContext(SharedInfoContext);
const { exitGateQR, updateExitGateQR } = useContext(SharedInfoContext);
const { signNumber, updateSignNumber } = useContext(SharedInfoContext);
const { registerNumber, updateRegisterNumber } = useContext(SharedInfoContext);
const { frontGateQR, updateFrontGateQR } = useContext(SharedInfoContext);
const { bandColor, updateBandColor } = useContext(SharedInfoContext);

const slotRef = useRef(null);
//アンマウント済みフラグ
const unmountedRef = useRef(false);
//プレビュー監視タイマー
const previewTimerRef = useRef(null);

//スキャン結果の受け取り口
//※常駐スキャナーへ登録するため、参照が変わらない関数にしておく
const onDetectedRef = useRef(null);
const scanHandlerRef = useRef((barcode) => {
	if (onDetectedRef.current) onDetectedRef.current(barcode);
});


//----------------------------------------
//scanbot：スキャン結果
//----------------------------------------

onDetectedRef.current = (barcode) => {
	const data = barcode?.text;
	if (!data) return;

	console.log("-------------------------");
	console.log("【scanbot】detected (QR)");
	console.log("format: " + barcode.format);
	console.log("data: " + data);
	console.log("-------------------------");

	//同一QRの連続検知を防ぐためデコード停止
	pauseDecoding();

	//スキャン不可
	props.setScanFlg(true);

	setQRcode(data);
	updateFrontGateQR(data);
	updateRegisterNumber(data);
};


//----------------------------------------
//scanbot：カメラ表示開始
//----------------------------------------
// カメラはスタート画面／スキャンボタン押下時に起動済みのため、
// ここでは常駐コンテナを画面内のスロットへ付け替えるだけ。
// 未起動の場合のみここで起動する。

const showCamera = async () => {
	//先にスロットへ付け替え（起動済みなら即座に表示される
	connectToElement(slotRef.current);

	const started = await startCamera();
	if (unmountedRef.current) return;

	if (!started) {
		onScanError(new Error("scanbot camera start failed"));
		return;
	}

	//起動直後はvideoが後から生成されるため、付け替えし直す
	connectToElement(slotRef.current);

	//デコード開始
	resumeDecoding();

	waitForCameraPreview();
};


//----------------------------------------
//scanbot：プレビュー表示検知
//----------------------------------------

const waitForCameraPreview = () => {
	if (previewTimerRef.current) {
		clearInterval(previewTimerRef.current);
		previewTimerRef.current = null;
	}

	let retry = 0;
	let revived = false;
	let relaidOut = false;

	previewTimerRef.current = setInterval(() => {
		if (unmountedRef.current) {
			clearInterval(previewTimerRef.current);
			previewTimerRef.current = null;
			return;
		}

		const video = slotRef.current?.querySelector("video");
		if (!video || video.readyState < 3 || video.videoWidth === 0) {
			retry++;
			const elapsed = retry * PREVIEW_WAIT_INTERVAL;

			//① videoが素材を手放しているだけなら、ストリームを付け直せば戻る
			if (!revived && elapsed >= PREVIEW_REVIVE_AFTER) {
				revived = true;
				if (reviveVideos(slotRef.current)) {
					console.log("【scanbot】videoが停止していたためストリームを付け直した");
				}
			}

			//② それでも出ない場合はスキャナーを組み直す（D-21と同じ手当て
			if (!relaidOut && elapsed >= PREVIEW_RELAYOUT_AFTER) {
				relaidOut = true;
				console.log("【scanbot】プレビューが出ないため組み直す");
				refreshLayout();
			}

			if (elapsed >= PREVIEW_WAIT_TIMEOUT) {
				clearInterval(previewTimerRef.current);
				previewTimerRef.current = null;
				//3条件のどれが欠けているかで対処が変わるため、状態を載せる
				const state = describePreviewState(slotRef.current, SCANBOT_CONTAINER_ID);
				console.log("【scanbot】camera preview timeout: " + state);
				onScanError(new Error("scanbot camera preview timeout / " + state));
			}
			return;
		}

		console.log("【scanbot】camera preview shown (QR)");

		clearInterval(previewTimerRef.current);
		previewTimerRef.current = null;

		//映像が出てからSDKにレイアウトを再計算させる
		//※これが無いと、映像到達前に計算された結果のままvideoが小さく残る端末がある
		refreshLayout();

	}, PREVIEW_WAIT_INTERVAL);
};


//----------------------------------------
//scanbot：スキャン画面から離れる
//----------------------------------------
// scandit と同じく、スキャン画面から離れたらカメラを閉じる

const hideCamera = () => {
	if (previewTimerRef.current) {
		clearInterval(previewTimerRef.current);
		previewTimerRef.current = null;
	}

	//デコード停止・コンテナ退避・カメラ停止（次の画面が使用中なら何もしない
	releaseScreen(scanHandlerRef.current, slotRef.current);
};




//----------------------------------------
//scanbotエラー表示
//----------------------------------------

const onScanError = (error) => {
	if (String(error).indexOf("NotAllowedError") !== -1 || String(error).indexOf("Permission denied") !== -1) {
		console.log("error: " + error);
		props.setErrFlg(true);
		props.setErr([true,``, process.env.REACT_APP_ERROR_MESSAGE_SCANDIT_NOTALLOW]);

		//ログアップロードAPI
		loguploadAPI(sharedInfo.transactionno,"scanbot:error, " + error);

	}else if(String(error) !== "undefined" && String(error) !== undefined && String(error) !== ""){
		console.log("error: " + error);
		props.setErrFlg(true);
		let err_msg = ";;"+error+";;;";
		props.setErr([true,``, err_msg]);

		//ログアップロードAPI
		loguploadAPI(sharedInfo.transactionno,"scanbot:error, " + error);
	}
}


//----------------------------------------
//初期化
//----------------------------------------

useEffect(() => {
	unmountedRef.current = false;

	document.getElementById('header').classList.add('is_behind');

	//arrows We 対応
	if(window.innerWidth >= 320){
		document.documentElement.style.zoom = window.innerWidth / 390;
		var zratio = window.innerWidth / 390;
	}else{
		document.documentElement.style.zoom = 1;
		var zratio = 1;
	}
	let hratio = window.innerHeight / (window.innerHeight * zratio);
	let realheight = window.innerHeight * hratio;
	let elementA = document.getElementById('qrscan');
	if (elementA) {
		elementA.style.height = realheight + 'px';
	}

	//スキャン結果の受け取り先として登録
	setScanHandler("qr", scanHandlerRef.current, onScanError);

	//カメラ表示（スタート画面で起動済みのカメラを使い回す
	showCamera();

	return () => {
		unmountedRef.current = true;
		hideCamera();
	};
}, []);


//----------------------------------------
//初回QR
//----------------------------------------

const [firstQR, setFirstQR] = useState(true);

useEffect(() => {
	if(firstQR === true){
		setFirstQR(false);
	}else{
		console.log("props.scanFlg: " + props.scanFlg);
		// props.scanFlg true: スキャン不可 false:スキャン可
		if(props.scanFlg === false && firstQR === false){
			setQRcode('');
			//デコード再開
			resumeDecoding();
		}
	}
}, [props.scanFlg]);


//----------------------------------------
//QRコード取得
//----------------------------------------

const [isApiCall, setIsApiCall] = useState(true);

useEffect(() => {
	console.log("QRcode: " + QRcode);
	console.log("isApiCall: " + isApiCall);
	if(QRcode !== ''){
	if (QRcode && isApiCall) {
		setIsApiCall(false);

		console.log("【売上一覧取得API call】---------------------------");
		saleslistAPI();

		//※このタイマーをクリーンアップで解除してはいけない。
		//　このeffectは QRcode が変わるたびに再実行され、その直前に前回のクリーンアップが走る。
		//　API呼び出しから1秒以内に QRcode が変化するとタイマーが解除され、
		//　isApiCall が false のまま戻らなくなり、以降ずっとAPIを呼べなくなる
		setTimeout(() => {
			setIsApiCall(true);
		}, 1000);
	}else {
		console.log("【売上一覧取得API call】再呼び出し制限中");

		//APIを呼ばずに抜けた場合はレスポンスもエラーモーダルも発生しないため、
		//検知時に止めたスキャン（pauseDecoding／setScanFlg(true)）を戻す契機が無く画面が固まる。
		//スキャン状態を戻して読み直せるようにする。
		//※通信中は戻さないこと。ここでスキャンを再開すると、決済処理が走っている最中に
		//　同じQRをもう一度読んでしまい、1秒の制限が明けた時点で saleslistAPI が再送され、
		//　続けて payreserveAPI / payAPI が二重に呼ばれる（二重転送）
		if(!saleslistLoading && !payreserveLoading && !payLoading){
			setQRcode('');
			props.setScanFlg(false);
		}
	}
	}
}, [QRcode]);


//----------------------------------------
//スキャン停止からの復帰（保険
//----------------------------------------
// 検知時は「スキャンを止める → 読み取り値をstateへ渡す」の順で処理している。
// scandit は停止処理が非同期（enableScanning(false) → enableCamera(false)）のため、
// 低スペック端末でここが失敗・遅延すると、値がstateへ渡らないまま終わることがある。
// その場合 scanFlg が true のまま戻す契機が無く、以降スキャンできなくなる。
// 「スキャンを止めているのに読み取り値が無い」状態が続いたら再開させる。
//
// ※ここでの clearTimeout は正しい。条件が変わったら判定をやり直したいため。
// 　上のAPI呼び出し制限のタイマーとは意図が逆なので混同しないこと

useEffect(() => {
	//スキャン可・エラーモーダル表示中・読み取り値ありは対象外
	if (props.scanFlg !== true) return;
	if (props.errFlg === true) return;
	if (QRcode !== '') return;

	const timer = setTimeout(() => {
		console.log("【scan】スキャンを止めたまま読み取り値が来ないため再開する");
		props.setScanFlg(false);
	}, SCAN_STALL_TIMEOUT);

	return () => clearTimeout(timer);
}, [props.scanFlg, props.errFlg, QRcode]);


//----------------------------------------
//ログアップロードAPI
//----------------------------------------

const { loguploadAPI, data: loguploadData, loading: loguploadLoading, error: loguploadError } = useUserAPI();

//ログアップロードAPI：レスポンス
useEffect(() => {
	if(loguploadData){
		console.log(loguploadData);
		if (loguploadData.isSuccess == true) { //SUCCESS
			console.log(`【loguploadAPI】 isSuccess: ${loguploadData.isSuccess}`);
		}else{ //ERROR
			console.log(`【loguploadAPI】 isSuccess: ${loguploadData.isSuccess}`);
			console.log(`【loguploadAPI】 errorCode: ${loguploadData.errorCode}`);
			console.log(`【loguploadAPI】 errorMessage: ${loguploadData.errorMessage}`);
		}
	}
}, [loguploadData]);


//----------------------------------------
//売上一覧取得API レスポンス取得
//----------------------------------------

useEffect(() => {
	if(saleslistData){
		if (saleslistData.data) { //SUCCESS

			props.setSoundState("se07_scan");

			console.log(`【saleslistAPI】 sumIncludeTaxPrice: ${saleslistData.data.sumIncludeTaxPrice}`);
			console.log(`【saleslistAPI】 sumSellPrice: ${saleslistData.data.sumSellPrice}`);
			console.log(`【saleslistAPI】 sumItemCount: ${saleslistData.data.sumItemCount}`);

			//売上一覧：明細用
			props.setSaleData(saleslistData);

			console.log("QRコード判定（先頭2桁）: " + QRcode.substring(0, 2));
			if(QRcode.substring(0, 2) === "79"){ //代表レジ
				console.log("代表レジ");
				console.log(`【payreserveAPI】 registerNumber: ${QRcode}`);
				//ログアップロードAPI
				loguploadAPI(sharedInfo.transactionno,"payreserveAPI:otb, code:" + props.otb);

				payreserveAPI(QRcode,props.otb);

				//「QRスキャン戻るボタン」無効
				setIsQRScanBack(false);

			}else{ //専用レジ（個別レジ）・有人レジ
				console.log("専用レジ（個別レジ）・有人レジ");
				// 15、16、17番目の文字を抽出
				const reginum = QRcode.substring(14, 17);
				console.log(`【payAPI】 registerNumber: ${reginum}`);
				updateRegisterNumber(reginum);
				//ログアップロードAPI
				loguploadAPI(sharedInfo.transactionno,"payAPI:otb, code:" + props.otb);

				payAPI(QRcode,props.otb);

				//「QRスキャン戻るボタン」無効
				setIsQRScanBack(false);
			}
		}else{ //ERROR
			console.log(`【saleslistAPI】 errorCode: ${saleslistData.errorCode}`);
			console.log(`【saleslistAPI】 errorMessage: ${saleslistData.errorMessage}`);
			console.log(`【saleslistAPI】 isSuccess: ${saleslistData.isSuccess}`);

			if(saleslistData.errorCode === "26"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${saleslistData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_26]);
			}else if(saleslistData.errorCode === "27"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${saleslistData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_27]);
			}else if(saleslistData.errorCode === "32"){//フロントチェックエラー
				props.setErrFlg(true);
				props.setErr([true,`${saleslistData.errorCode}`,`${saleslistData.errorMessage}`]);
			}else{
				props.setErrFlg(true);
				props.setErr([true,`${saleslistData.errorCode}`,`${saleslistData.errorMessage}`]);
			}
		}
	}
	if(saleslistError){
		console.log("saleslistError");
		console.log(saleslistError);
		props.setErrFlg(true);
		props.setErr([true,``, process.env.REACT_APP_ERROR_MESSAGE_TIMEOUT]);
	}
}, [saleslistData,saleslistLoading,saleslistError]);


//----------------------------------------
//決済予約API：代表レジ レスポンス取得
//----------------------------------------

useEffect(() => {
	if(payreserveData){
		if (payreserveData.data) { //SUCCESS
			console.log(`【payreserveAPI】 qrcode: ${payreserveData.data.qrcode}`);
			console.log(`【payreserveAPI】 signNumber: ${payreserveData.data.signNumber}`);
			console.log(`【payreserveAPI】 registerNumber: ${payreserveData.data.registerNumber}`);
			if(process.env.REACT_APP_FRONT_CHECK === "true"){ //せき止め有効
				console.log(`【payreserveAPI】 dammingFlag: ${payreserveData.data.dammingFlag}`);
			}

			if(QRcode.substring(12, 14) === "01"){ //有人レジ
				console.log("有人レジへ");
			}else if(QRcode.substring(12, 14) === "02"){ //兼用セルフ（有人レジと同じ挙動
				console.log("有人レジへ（兼用セルフ");
			}else if(payreserveData.data.qrcode === null){ //代表レジ 待ちなし精算確認APIへ
				console.log("代表レジ 待ちなし算確認APIへ");
			}else{ //代表レジ 待ちあり精算確認APIへ
				console.log("代表レジ 待ちあり精算確認APIへ");
			}
			updateExitGateQR(payreserveData.data.qrcode);
			updateSignNumber(payreserveData.data.signNumber);
			updateRegisterNumber(payreserveData.data.registerNumber);
			setScanItem(true);


			setTimeout(function(){
				setQrtxtHide(true);
			}, 1000);
			setTimeout(function(){
				setScanItem(false);
				if(props.page === "qrscan"){
					console.log(QRcode.substring(0, 2));
					if(QRcode.substring(0, 2) === "79"){ //代表レジ
						if(process.env.REACT_APP_FRONT_CHECK === "true"){ //せき止め有効
							if(payreserveData.data.dammingFlag === "1"){ //せき止めON
								goRegiSenyou(true);
							}else{ //せき止めOFF
								goRegiSenyou(false);
							}
						}else{ //せき止め無効
							goRegiSenyou(false);
						}
					}else{ //専用レジ（個別レジ）・有人レジ
						console.log("QRcode.substring(12, 14): "+QRcode.substring(12, 14));
						if(QRcode.substring(12, 14) === "01"){ //有人レジ
							goRegiYujin();
						}else if(QRcode.substring(12, 14) === "02"){ //兼用セルフ（有人レジと同じ挙動
							goRegiYujin();
						}else{ //専用レジ（個別レジ）へ
							goRegiSelf();
						}
					}
				}
			}, 2000);

		}else{ //ERROR
			console.log(`【payreserveAPI】 errorCode: ${payreserveData.errorCode}`);
			console.log(`【payreserveAPI】 errorMessage: ${payreserveData.errorMessage}`);
			console.log(`【payreserveAPI】 isSuccess: ${payreserveData.isSuccess}`);

			//「QRスキャン戻るボタン」有効
			setIsQRScanBack(true);

			if(payreserveData.errorCode === "26"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${payreserveData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_26]);
			}else if(payreserveData.errorCode === "27"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${payreserveData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_27]);
			}else if(payreserveData.errorCode === "32"){//フロントチェックエラー
				console.log("エラー32：フロントチェックエラー");
				props.setPage("error_32");
			}else{
				props.setErrFlg(true);
				props.setErr([true,`${payreserveData.errorCode}`,`${payreserveData.errorMessage}`]);
			}
		}
	}
	if(payreserveError){ //通信エラー・タイムアウト（緑ルート
		console.log("payreserveError");
		console.log(payreserveError);
		console.log("タイムアウト（ノーレスポンス）・通信エラーのため、エラー35画面へ");
		props.setPage("error_35");
	}
}, [payreserveData,payreserveLoading,payreserveError]);


//----------------------------------------
//決済API：専用レジ（個別レジ）・有人レジ レスポンス取得
//----------------------------------------

useEffect(() => {
	if(payData){
		if (payData.data) { //SUCCESS
			console.log(`【payAPI】 qrcode: ${payData.data.qrcode}`);

			if(QRcode.substring(12, 14) === "01"){ //有人レジ
				console.log("有人レジへ");
			}else if(QRcode.substring(12, 14) === "02"){ //兼用セルフ（有人レジと同じ挙動
				console.log("有人レジへ（兼用セルフ");
			}else if(payData.data.qrcode === null){ //専用レジ（個別レジ）待ちなし精算確認APIへ
				console.log("専用レジ（個別レジ）待ちなし算確認APIへ");
			}else{ //専用レジ（個別レジ）待ちあり精算確認APIへ
				console.log("専用レジ（個別レジ）待ちあり精算確認APIへ");
			}
			updateExitGateQR(payData.data.qrcode);
			setScanItem(true);


			setTimeout(function(){
				setQrtxtHide(true);
			}, 1000);
			setTimeout(function(){
				setScanItem(false);
				if(props.page === "qrscan"){
					console.log(QRcode.substring(0, 2));
					if(QRcode.substring(0, 2) === "79"){ //代表レジ
						//せき止め無効
						goRegiSenyou(false);
					}else{ //専用レジ（個別レジ）・有人レジ
						console.log("QRcode.substring(12, 14): "+QRcode.substring(12, 14));
						if(QRcode.substring(12, 14) === "01"){ //有人レジ
							goRegiYujin();
						}else if(QRcode.substring(12, 14) === "02"){ //兼用セルフ（有人レジと同じ挙動
							goRegiYujin();
						}else{ //専用レジ（個別レジ）へ
							goRegiSelf();
						}
					}
				}
			}, 2000);


		}else{ //ERROR
			console.log(`【payAPI】 errorCode: ${payData.errorCode}`);
			console.log(`【payAPI】 errorMessage: ${payData.errorMessage}`);
			console.log(`【payAPI】 isSuccess: ${payData.isSuccess}`);

			//「QRスキャン戻るボタン」有効
			setIsQRScanBack(true);

			if(payData.errorCode === "26"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${payData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_26]);
			}else if(payData.errorCode === "27"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${payData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_27]);
			}else if(payData.errorCode === "32"){//フロントチェックエラー
				console.log("エラー32：フロントチェックエラー");
				props.setPage("error_32");
			}else{
				props.setErrFlg(true);
				props.setErr([true,`${payData.errorCode}`,`${payData.errorMessage}`]);
			}
		}
	}
	if(payError){ //通信エラー・タイムアウト（緑ルート
		console.log("payError");
		console.log(payError);
		console.log("タイムアウト（ノーレスポンス）・通信エラーのため、エラー35画面へ");
		props.setPage("error_35");
	}
}, [payData,payLoading,payError]);


//----------------------------------------
//ページ遷移
//----------------------------------------

//「QRスキャン戻るボタン」の抑制フラグ true：戻るボタン有効 false：戻るボタン無効
const [isQRScanBack, setIsQRScanBack] = useState(true);

const onScanClose = () => {
	if(isQRScanBack !== false){
		//カメラを閉じてコンテナを退避
		hideCamera();
		setScanItem(false);
		props.setPage("shopping");
		props.setSoundState("se08_se");
	}
}

//代表レジ
const goRegiSenyou = (dammingFlg) => {
	//カメラを閉じてコンテナを退避
	hideCamera();
	props.setRegiType("regi_senyou");

	if(process.env.REACT_APP_IAEON_FLG === "true"){ //iAEON有効
		//データ保存API（買物データ削除
		callAPI_18_delete();
	}

	if(process.env.REACT_APP_FRONT_CHECK === "true"){ //せき止め有効
		if(dammingFlg === true){ //せき止めON
			props.setPage("front_check");
		}else{ //せき止めOFF
			props.setPage("regi_senyou");
		}
	}else{ //せき止め無効
		props.setPage("regi_senyou");
	}
}

//専用レジ（個別レジ
const goRegiSelf = () => {
	//カメラを閉じてコンテナを退避
	hideCamera();
	props.setRegiType("regi_self");

	if(process.env.REACT_APP_IAEON_FLG === "true"){ //iAEON有効
		//データ保存API（買物データ削除
		callAPI_18_delete();
	}

	props.setPage("regi_self");
}

//有人レジ
const goRegiYujin = () => {
	//カメラを閉じてコンテナを退避
	hideCamera();
	props.setRegiType("regi_yujin");

	if(process.env.REACT_APP_IAEON_FLG === "true"){ //iAEON有効
		//データ保存API（買物データ削除
		callAPI_18_delete();
	}

	props.setPage("regi_yujin");
}

const [scanItem, setScanItem] = useState(false)


//----------------------------------------
//アニメーション設定
//----------------------------------------

const motionConfig = {
	initial: {
		y: 0,
		opacity: 0,
	},
	animate: {
		y: 0,
		opacity: 1,
	},
	hidden: {
		y: 0,
		opacity: 0,
	},
};

const [qrtxtHide, setQrtxtHide] = useState(false);


//----------------------------------------
//データ保存API（買物データ削除
//----------------------------------------

//18.データ保存API（買物データ削除
function callAPI_18_delete(){
	console.log("----------------------------");
	console.log("【iAeonSetData】post");
	let successCall_18 = function(success){
		var aaa = success;
		console.log('【iAeonSetData】success：');
		console.log(aaa);
	}
	let errorCall_18 = function(error){
		var bbb = error;
		console.log('【iAeonSetData】error: ' + error.error_code + " : " + error.message);
		console.log(bbb);
	}
	iAeonSetData({"mode":"delete", "key": "shopping_data"}).then(successCall_18).catch(errorCall_18);
}


return (
<div id="qrscan" className="container l_contents">

<header id="header02" className={`l_header02 ${bandColor}`}>
	<button href="" className={`header02_backBtn ${isQRScanBack ? "" : "is_disabled"}`} onClick={onScanClose}>
		<img src="/images/icn_back01_white.svg" alt="" width="24" height="24"/>
	</button>
	<p className="header02_tit">お支払い中</p>
</header>

{/* <!--- #qrScanBox ---> */}

<div id="qrScanBox" className={styles.qrScanBox}>
	<p id="qrtxt" className={`${styles.qrScanBox_tit} ${qrtxtHide ? styles.is_hide : ''}`}>お支払いコードをスキャン</p>
	<p className={styles.qrScanBox_txt}>
		<span className={styles.qrScanBox_stit}>スキャン忘れの<br />商品はございませんか？</span>
		従業員が商品点数を<br />確認させていただくことがございます
	</p>
	<div id="barcode-scanner-area" className={styles.qrScanArea}></div>
	<div className={styles.qrScanCover}></div>
	<div ref={slotRef} className={styles.sdkScanView}></div>
	<div className={styles.sdkScanName}>scanbot + {cameraResolution}</div>
	<AnimatePresence>
		{scanItem && (
			<motion.p id="addItem" className={styles.qrScan_success}
			initial={'initial'}
			animate={'animate'}
			exit={'hidden'}
			variants={motionConfig}
			transition={{duration: 0.4, ease: 'easeOut'}}
			>
				<span className={styles.qrScan_success01}>
					<span className={styles.qrScan_success_check}>
						<img src="/images/icn_check_aeon.svg" alt="" width="24" height="24" />
					</span>
					お支払いコードを読み取りました
				</span>
				<span className={styles.qrScan_success02}>
					<span className={styles.qrScan_success_spinner}>
						<img src="/images/loading_spinner.png" alt="" width="24" height="24" />
					</span>
					レジへ送信しています
				</span>
			</motion.p>
		)}
	</AnimatePresence>
</div>

</div>
	)
}

export default QRScanbotScanner;
